import axios from 'axios';
import { getFunctions, httpsCallable } from 'firebase/functions';

// Interface định nghĩa cấu trúc cho tin nhắn trò chuyện
export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

// Interface cho kết quả trả về từ API
interface ChatGPTResponse {
  id: string;
  choices: {
    message: {
      role: string;
      content: string;
    };
    index: number;
    finish_reason: string;
  }[];
}

/**
 * Phương thức gọi API ChatGPT trực tiếp
 * Chỉ sử dụng cho môi trường phát triển hoặc khi có API key an toàn
 */
export const fetchAIResponseDirect = async (
  userMessage: string, 
  previousMessages: ChatMessage[] = []
): Promise<string> => {
  try {
    // Lấy API key từ biến môi trường (cần cấu hình trong .env file)
    const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
    
    if (!apiKey) {
      throw new Error('Missing OpenAI API key. Please set REACT_APP_OPENAI_API_KEY in .env file.');
    }

    // Tạo context cho ChatGPT
    const messages: ChatMessage[] = [
      {
        role: 'system',
        content: `Bạn là trợ lý ảo chuyên nghiệp của Bookdi, một nền tảng kết nối đối tác cung cấp dịch vụ với khách hàng tại Việt Nam.
        Bạn phải luôn nói bằng tiếng Việt và hỗ trợ các đối tác về:
        
        # THÔNG TIN CHÍNH:
        1. Quy trình đăng ký trên nền tảng (6 bước: Tài khoản, thông tin doanh nghiệp, dịch vụ cung cấp, quy mô đội ngũ, địa chỉ và chọn gói dịch vụ)
        2. Chi tiết các gói dịch vụ (Miễn phí: 0đ, Dịch vụ cao cấp: 5.000đ)
        3. Quy trình duyệt đơn và thời gian chờ (24-48 giờ)
        4. Các tính năng nền tảng và lợi ích khi sử dụng Bookdi
        5. Hỗ trợ kỹ thuật khi gặp vấn đề trong quá trình đăng ký
        
        # GÓI DỊCH VỤ CHI TIẾT:
        - Gói Miễn Phí (0đ):
          + Đăng ký và tạo hồ sơ doanh nghiệp cơ bản
          + Hiển thị trên nền tảng tìm kiếm Bookdi
          + Quản lý lịch hẹn cơ bản (khách hàng đặt lịch trực tuyến)
          + Hạn chế: Chỉ đăng được tối đa 1 dịch vụ, không có công cụ marketing, chỉ có 1 tài khoản nhân viên

        - Gói Dịch Vụ Cao Cấp (5.000đ):
          + Tất cả tính năng của gói miễn phí
          + Đăng tối đa 10 dịch vụ trên nền tảng
          + Quản lý lịch hẹn nâng cao với thông báo SMS
          + Thêm tối đa 5 nhân viên vào hệ thống
          + Công cụ marketing và quảng cáo trong hệ sinh thái Bookdi
          + Báo cáo doanh thu, hiệu suất theo thời gian thực
        
        # LỢI ÍCH KHI THAM GIA BOOKDI:
        - Tiếp cận cộng đồng người dùng lớn (10.000+ người dùng)
        - Tích hợp hệ sinh thái truyền thông đa nền tảng
        - Hỗ trợ thiết kế hình ảnh bài đăng, video review cơ bản
        - Tối ưu chi phí marketing và vận hành
        
        # THÔNG TIN LIÊN HỆ:
        - Email: info@bookdi.net
        - Hotline: 0385858193
        - Website: bookdi.net
        
        Giọng điệu thân thiện, chuyên nghiệp và tự tin. Câu trả lời ngắn gọn (dưới 200 từ), dễ hiểu, chính xác.
        Luôn hướng đến giải quyết vấn đề của người dùng. Nếu không biết câu trả lời chính xác, đề nghị liên hệ info@bookdi.net.`
      },
      ...previousMessages,
      {
        role: 'user',
        content: userMessage
      }
    ];

    // Gọi API ChatGPT
    const response = await axios.post(
      'https://api.openai.com/v1/chat/completions',
      {
        model: 'ft:gpt-3.5-turbo-0125:bookdi-faq::8FYMeHpu',
        messages,
        max_tokens: 300,
        temperature: 0.7,
      },
      {
        headers: {
          'Authorization': `Bearer ${apiKey}`,
          'Content-Type': 'application/json'
        }
      }
    );

    return response.data.choices[0].message.content;
  } catch (error) {
    console.error('Error calling ChatGPT API:', error);
    throw error;
  }
};

/**
 * Phương thức gọi API ChatGPT thông qua Firebase Functions
 * Phương pháp an toàn hơn để bảo vệ API key
 */
export const fetchAIResponseSecure = async (
  userMessage: string, 
  previousMessages: ChatMessage[] = []
): Promise<string> => {
  try {
    const functions = getFunctions();
    const getChatResponse = httpsCallable<
      { message: string; previousMessages: ChatMessage[] },
      { success: boolean; data: string; error?: string }
    >(functions, 'getChatbotResponse');

    console.log('Gọi Cloud Function với:', { message: userMessage, previousMessages });

    const result = await getChatResponse({
      message: userMessage,
      previousMessages
    });

    console.log('Nhận kết quả từ Cloud Function:', result.data);

    if (!result.data.success) {
      console.error('Lỗi từ Cloud Function:', result.data.error);
      throw new Error(result.data.error || 'Unknown error occurred');
    }

    return result.data.data;
  } catch (error) {
    console.error('Error calling ChatGPT via Firebase Functions:', error);
    throw error;
  }
};

/**
 * Chức năng backup khi API gặp vấn đề
 * Trả về câu trả lời dựa trên các quy tắc đơn giản
 */
export const getLocalResponse = (message: string): string => {
  const lowerMessage = message.toLowerCase();

  // Danh sách từ khóa và phản hồi tương ứng
  const keywordResponses = [
    {
      keywords: ['đăng ký', 'đối tác', 'bước', 'quy trình', 'tham gia'],
      response: 'Để đăng ký làm đối tác với Bookdi, bạn cần hoàn thành form đăng ký gồm 6 bước: (1) Tài khoản, (2) thông tin doanh nghiệp, (3) dịch vụ cung cấp, (4) quy mô đội ngũ, (5) địa chỉ và (6) chọn gói dịch vụ. Quy trình đăng ký mất khoảng 5-10 phút để hoàn thành. Bạn cần hỗ trợ ở bước nào cụ thể?'
    },
    {
      keywords: ['phí', 'giá', 'gói', 'chi phí', 'thanh toán', 'dịch vụ cao cấp'],
      response: 'Bookdi có 2 gói dịch vụ: (1) Gói miễn phí: Hoàn toàn miễn phí, đăng 1 dịch vụ, 1 tài khoản nhân viên, quản lý lịch hẹn cơ bản. (2) Gói dịch vụ cao cấp: 5.000đ, đăng 10 dịch vụ, thêm 5 nhân viên, quản lý lịch hẹn nâng cao với SMS, công cụ marketing, báo cáo doanh thu thời gian thực.'
    },
    {
      keywords: ['thời gian', 'duyệt', 'xét duyệt', 'chờ', 'được phê duyệt'],
      response: 'Thông tin đăng ký của bạn sẽ được xem xét trong vòng 24-48 giờ làm việc. Sau khi được duyệt, bạn sẽ nhận được email xác nhận và có thể bắt đầu sử dụng nền tảng. Trong thời gian chờ duyệt, bạn có thể liên hệ info@bookdi.net nếu có bất kỳ thắc mắc nào.'
    },
    {
      keywords: ['lợi ích', 'ưu điểm', 'tại sao', 'tính năng'],
      response: 'Khi tham gia Bookdi, bạn sẽ được: (1) Tiếp cận cộng đồng người dùng lớn (10.000+ người dùng), (2) Tích hợp vào hệ sinh thái truyền thông đa nền tảng, (3) Hỗ trợ thiết kế hình ảnh và video review cơ bản, (4) Tối ưu chi phí marketing và vận hành, (5) Tăng lượng khách hàng đến từ online mà không cần thuê thêm nhân sự.'
    },
    {
      keywords: ['liên hệ', 'hỗ trợ', 'giúp đỡ', 'email', 'hotline'],
      response: 'Nếu bạn cần hỗ trợ thêm, vui lòng liên hệ với chúng tôi qua: Email: info@bookdi.net | Hotline: 0385858193 | Website: bookdi.net. Đội ngũ Bookdi sẽ phản hồi trong thời gian sớm nhất.'
    },
    {
      keywords: ['cảm ơn', 'thanks', 'thank'],
      response: 'Rất vui khi được hỗ trợ bạn! Nếu có thắc mắc gì thêm về quy trình đăng ký hoặc các tính năng của Bookdi, đừng ngần ngại hỏi tôi. Chúc bạn một ngày tốt lành!'
    },
    {
      keywords: ['kỹ thuật', 'lỗi', 'vấn đề', 'không đăng ký được', 'trục trặc'],
      response: 'Nếu bạn gặp vấn đề kỹ thuật trong quá trình đăng ký, vui lòng thử: (1) Làm mới trình duyệt, (2) Xóa cache, (3) Thử trình duyệt khác như Chrome hoặc Firefox. Nếu vẫn không được, vui lòng chụp màn hình lỗi và gửi đến info@bookdi.net để được hỗ trợ trực tiếp.'
    },
    {
      keywords: ['quảng cáo', 'marketing', 'tiếp thị', 'seo', 'quảng bá'],
      response: 'Bookdi hỗ trợ đối tác tiếp cận khách hàng thông qua: (1) Hiển thị nổi bật trên nền tảng tìm kiếm Bookdi, (2) Quảng bá qua hệ sinh thái truyền thông (Fanpage 135.000+ người theo dõi, Group 140.000+ thành viên), (3) Hỗ trợ thiết kế hình ảnh bài đăng, video review cơ bản, (4) Hướng dẫn viết nội dung thu hút khách hàng.'
    },
    {
      keywords: ['lịch hẹn', 'quản lý', 'đặt lịch', 'booking'],
      response: 'Hệ thống đặt lịch của Bookdi cho phép khách hàng đặt lịch trực tuyến 24/7. Gói miễn phí có quản lý lịch hẹn cơ bản, trong khi gói cao cấp bổ sung tính năng thông báo SMS, lịch sử đặt lịch chi tiết, và cho phép nhiều nhân viên cùng quản lý lịch hẹn. Điều này giúp tối ưu hóa thời gian và không bỏ lỡ khách hàng tiềm năng.'
    },
    {
      keywords: ['địa chỉ', 'vị trí', 'cố định', 'địa điểm'],
      response: 'Nếu doanh nghiệp của bạn không có địa chỉ cố định (như dịch vụ tại nhà), bạn vẫn có thể đăng ký làm đối tác Bookdi. Trong bước "Địa chỉ" của form đăng ký, hãy chọn tùy chọn "Không có địa chỉ cố định" và hệ thống sẽ điều chỉnh phù hợp với loại hình kinh doanh của bạn.'
    }
  ];

  // Kiểm tra từng nhóm từ khóa
  for (const item of keywordResponses) {
    if (item.keywords.some(keyword => lowerMessage.includes(keyword))) {
      return item.response;
    }
  }

  // Câu trả lời mặc định nếu không có từ khóa nào phù hợp
  return 'Chào mừng bạn đến với Bookdi! Tôi là trợ lý ảo được hỗ trợ bởi AI, tôi có thể giúp bạn với thông tin về quy trình đăng ký đối tác, các gói dịch vụ, tính năng của nền tảng và giải đáp các thắc mắc khác. Bạn cần hỗ trợ điều gì cụ thể?';
};

/**
 * Phương thức chính để lấy phản hồi từ chatbot
 * Thử gọi API trước, nếu thất bại thì dùng phản hồi cục bộ
 */
export const getChatbotResponse = async (
  userMessage: string, 
  previousMessages: ChatMessage[] = []
): Promise<string> => {
  try {
    // Thử sử dụng phương thức an toàn qua Firebase Functions nếu có
    try {
      return await fetchAIResponseSecure(userMessage, previousMessages);
    } catch (secureError) {
      console.warn('Secure API call failed, trying direct method:', secureError);
      
      // Nếu không được, thử gọi trực tiếp API ChatGPT
      return await fetchAIResponseDirect(userMessage, previousMessages);
    }
  } catch (error) {
    console.warn('All API calls failed, falling back to local response:', error);
    
    // Nếu tất cả đều thất bại, dùng phản hồi cục bộ
    return getLocalResponse(userMessage);
  }
}; 